import { UtilityFunctions } from '@copilot/common/utils/common';
import {
    createContext,
    PropsWithChildren,
    RefObject,
    useContext,
    useReducer,
    useRef,
    useState,
} from 'react';
import { RichTextEditorRef } from '@copilot/common/components/editors/richTextEditor/types/richTextEditorTypes';
import { FeedbackOption } from '@copilot/data/responses/interface';
import { ReplyRecommendationHookData } from '@copilot/common/hooks/replyRecommendation/types';
import { isEmpty } from 'lodash';

export type TextEditorStateType = {
    /**
     * Text in the text editor
     */
    text: string;
    /**
     * Automated step's id if the current text is from an automated step
     */
    automatedStepId?: string;
    /**
     * Quick responses template id if the current text is from quick response
     */
    quickResponseId?: string;
    /**
     * Whether the text editor is disabled
     */
    disabled: boolean;
};

/**
 * Available action types for updating textEditor context
 */
export const TextEditorContextActions = {
    setText: 'setText',
    setAutomatedStep: 'setAutomatedStep',
    setQuickResponse: 'setQuickResponse',
    setRecommendedMessage: 'setRecommendedMessage',
    reset: 'reset',
} as const;

/**
 * Actions available for dispatching to update textEditor context
 */
type TextEditorReducerActionType =
    | {
          type: typeof TextEditorContextActions.setText;
          payload: Pick<TextEditorStateType, 'text'>;
      }
    | {
          type: typeof TextEditorContextActions.setAutomatedStep;
          payload: Pick<TextEditorStateType, 'text' | 'automatedStepId'>;
      }
    | {
          type: typeof TextEditorContextActions.setQuickResponse;
          payload: Pick<TextEditorStateType, 'text' | 'quickResponseId'>;
      }
    | {
          type: typeof TextEditorContextActions.setRecommendedMessage;
          payload: Pick<TextEditorStateType, 'text'>;
      }
    | {
          type: typeof TextEditorContextActions.reset;
      };

/**
 * Reducer for updating textEditor context
 * @param state The previous state
 * @param action The action we want to perform
 * @returns The updated state.
 */
function textEditorReducer(
    state: TextEditorStateType,
    action: TextEditorReducerActionType
): TextEditorStateType {
    switch (action.type) {
        case TextEditorContextActions.setText:
            return { ...state, text: action.payload.text };
        case TextEditorContextActions.setAutomatedStep:
            return {
                ...state,
                text: action.payload.text,
                automatedStepId: action.payload.automatedStepId,
            };
        case TextEditorContextActions.setQuickResponse:
            return {
                ...state,
                text: action.payload.text,
                quickResponseId: action.payload.quickResponseId,
            };
        case TextEditorContextActions.setRecommendedMessage:
            return {
                ...state,
                text: action.payload.text,
                quickResponseId: undefined,
            };
        case TextEditorContextActions.reset:
            return { ...state, text: '', quickResponseId: undefined, automatedStepId: undefined };
        default:
            return UtilityFunctions.assertUnreachable(action);
    }
}

type TextEditorContextType = TextEditorStateType & {
    /**
     * Update TextEditorContext
     * @param action The action to dispatch
     */
    dispatchUpdate: (action: TextEditorReducerActionType) => void;
    /**
     * Reference to the RichTextEditor component
     */
    editorRef: RefObject<RichTextEditorRef> | null;
    /** Recommended message to show */
    recommendedMessage?: string;
    /** Recommended feedback to show */
    recommendedFeedback?: FeedbackOption;
    /** Whether the recommended message is active */
    isRecommendedMessageActive: boolean;
    /** Thread ID for the conversation */
    threadId?: string;
    /** Callback for regenerating recommended message */
    onRegenerateRecommendedMessage?: () => Promise<boolean>;
    /** Whether regeneration is in progress */
    isRegeneratingRecommendedMessage: boolean;
};
const TextEditorContext = createContext<TextEditorContextType | undefined>(undefined);

/** Context provider for Text Editor Component */
export const TextEditorContextProvider = (
    props: PropsWithChildren<{
        value: string;
        disabled?: boolean;
        automatedStepId?: string;
        quickResponseId?: string;
        recommendedMessage?: string;
        recommendedFeedback?: FeedbackOption;
        isReplyRecommendationsEnabled?: boolean;
        threadId?: string;
        replyRecommendationHookData?: ReplyRecommendationHookData;
    }>
): JSX.Element => {
    const {
        value,
        disabled = false,
        children,
        automatedStepId,
        quickResponseId,
        recommendedMessage: initialRecommendedMessage,
        recommendedFeedback,
        isReplyRecommendationsEnabled = true,
        threadId,
        replyRecommendationHookData,
    } = props;
    const [state, dispatchUpdate] = useReducer(textEditorReducer, {
        text: value,
        disabled,
        automatedStepId,
        quickResponseId,
    });
    const editorRef = useRef<RichTextEditorRef>(null);

    const { regenerateMessage, isRegenerating } = replyRecommendationHookData ?? {};
    const [recommendedMessage, setRecommendedMessage] = useState<string | undefined>(
        initialRecommendedMessage
    );

    async function onRegenerateRecommendedMessage() {
        if (!regenerateMessage) {
            return false;
        }

        const newText = await regenerateMessage();
        if (newText) {
            setRecommendedMessage(newText);
            dispatchUpdate({
                type: TextEditorContextActions.setText,
                payload: { text: newText },
            });
        }
        return !isEmpty(newText);
    }

    return (
        <TextEditorContext.Provider
            value={{
                ...state,
                disabled,
                dispatchUpdate,
                editorRef,
                recommendedMessage: isReplyRecommendationsEnabled ? recommendedMessage : undefined,
                recommendedFeedback: isReplyRecommendationsEnabled
                    ? recommendedFeedback
                    : undefined,
                isRecommendedMessageActive:
                    isReplyRecommendationsEnabled &&
                    recommendedMessage !== undefined &&
                    recommendedMessage === state.text,
                threadId,
                onRegenerateRecommendedMessage,
                isRegeneratingRecommendedMessage: isRegenerating ?? false,
            }}
        >
            {children}
        </TextEditorContext.Provider>
    );
};

/** hook to get Text Editor Context */
export const useTextEditorContext = (): TextEditorContextType => {
    const context = useContext(TextEditorContext);
    if (context === undefined) {
        throw new Error('useTextEditorContext must be used within TextEditorContextProvider');
    }
    return context;
};
